import React from "react";
import { buildCurrentProjectSnapshot, buildSceneConfig } from "../../utils/environmentBundle";
import { detectEnvConfigXmlFingerprintMismatch } from "../../rl/envExport";
import {
  buildDeploymentBundleZip,
  downloadBuiltDeploymentBundle,
} from "../../utils/deploymentExport";
import { useFileStore } from "../../stores/fileStore";
import { getProjectSaveActions } from "../../stores/projectSaveStore";
import { getSceneState } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { buildProjectSaveSnapshotHash } from "../../utils/projectSaveSnapshot";
import { PROJECT_SAVE_REQUEST_EVENT } from "../../utils/projectSaveRequests";
import {
  appendExportReviewWarning,
  applyProjectNameToMjcfXml,
  buildMjcfExportReview,
  simplifyBlankProjectReview,
  confirmUnverifiableLocalProjectSave,
  createRobotOnlyMjcfXml,
  createStandaloneMjcfSceneXml,
  downloadEnvironmentBundleFromExport,
  estimateEnvironmentBundleSize,
  downloadPortableMjcfBundle,
  downloadPortableMjcfSceneBundle,
  downloadTextFile,
  getProjectIntegrityFailureReason,
  liftMjcfWorldBodies,
  normalizeExportProjectName,
  suggestExportProjectName,
  toExportFilenameBase,
} from "./exportUtils";

export function useMainPanelExports({ onOpenFeedbackPanel, onOpenUpdatesPanel }) {
  const { actions: fileActions } = useFileStore.getState();
  const projectName = useFileStore((state) => state.projectName);
  const [pendingDeploymentBundle, setPendingDeploymentBundle] = React.useState(null);
  const [pendingMjcfExport, setPendingMjcfExport] = React.useState(null);
  const [includeMjcfDesktopScene, setIncludeMjcfDesktopScene] = React.useState(true);
  const [pendingEnvironmentExport, setPendingEnvironmentExport] = React.useState(null);
  const [pendingKeyframeMismatchExport, setPendingKeyframeMismatchExport] = React.useState(null);
  const [quickProjectSavePending, setQuickProjectSavePending] = React.useState(false);
  const [exportProjectName, setExportProjectName] = React.useState(() =>
    suggestExportProjectName(useFileStore.getState().projectName, getSceneState())
  );
  const pendingProjectSaveRequestRef = React.useRef(null);

  const updateSuggestedExportProjectName = React.useCallback((nextProjectName) => {
    setExportProjectName(suggestExportProjectName(nextProjectName, getSceneState()));
  }, []);

  const refreshSuggestedExportProjectName = React.useCallback(() => {
    updateSuggestedExportProjectName(useFileStore.getState().projectName);
  }, [updateSuggestedExportProjectName]);

  const resolvePendingProjectSaveRequest = React.useCallback((saved) => {
    const resolve = pendingProjectSaveRequestRef.current;
    pendingProjectSaveRequestRef.current = null;
    if (typeof resolve === "function") resolve(Boolean(saved));
  }, []);

  const markCurrentProjectSaved = React.useCallback(() => {
    getProjectSaveActions().markProjectSaved(
      buildProjectSaveSnapshotHash({
        scene: getSceneState(),
        fileState: useFileStore.getState(),
      }),
      "local-download"
    );
  }, []);

  const showProjectDownloadCompleteToast = React.useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: "Download complete.",
          icon: "download",
        },
      })
    );
  }, []);

  const validateKeyframesForExport = React.useCallback(() => {
    const fileStoreState = useFileStore.getState();
    const sceneState = getSceneState();
    const expected = Object.values(sceneState.joints || {}).reduce((count, joint) => {
      const kind = String(joint?.kind || "").toLowerCase();
      if (kind === "free") return count + 7;
      if (kind === "ball") return count + 4;
      if (kind === "hinge" || kind === "slide") return count + 1;
      return count;
    }, 0);
    if (expected <= 0) return [];
    return Object.entries(fileStoreState.keyframes || {})
      .map(([name, qpos]) => ({
        name,
        actual: Array.isArray(qpos) ? qpos.length : 0,
        expected,
      }))
      .filter((item) => item.actual !== item.expected);
  }, []);

  const handleExportMjcfXml = React.useCallback(
    async (skipKeyframeNames = []) => {
      const skippedKeyframes = Array.isArray(skipKeyframeNames) ? skipKeyframeNames : [];
      if (skippedKeyframes.length === 0) {
        const invalid = validateKeyframesForExport();
        if (invalid.length > 0) {
          setPendingKeyframeMismatchExport({ type: "mjcf", invalid });
          return;
        }
      }
      try {
        const fileStoreState = useFileStore.getState();
        updateSuggestedExportProjectName(fileStoreState.projectName);
        const xml = await fileStoreState.actions.generateXmlFromState({
          startupPosePolicy: "currentScenePose",
          skipKeyframeNames: skippedKeyframes,
        });
        const review = buildMjcfExportReview(
          xml,
          fileStoreState.fileMap || {},
          fileStoreState.exportWarnings || []
        );
        const referencedAssets = review.counts?.referencedAssets ?? 0;
        setPendingMjcfExport({
          filename: "physics_generated.xml",
          bundleFilename: "mujoco_xml_assets_bundle.zip",
          xml,
          fileMap: fileStoreState.fileMap || {},
          liftModelToFloor: false,
          review: {
            ...review,
            summary:
              referencedAssets > 0
                ? `This MuJoCo export references ${referencedAssets} asset file(s). The default download includes them in a portable folder.`
                : "This MuJoCo export does not reference external assets. The default download still uses a MuJoCo-ready folder.",
          },
        });
      } catch (e) {
        logger.error("[main] failed to export MJCF XML:", e);
        updateSuggestedExportProjectName(useFileStore.getState().projectName);
        setPendingMjcfExport({
          filename: "physics_generated.xml",
          bundleFilename: "mujoco_xml_assets_bundle.zip",
          xml: "",
          fileMap: {},
          review: {
            status: "blocked",
            summary: "The XML could not be generated.",
            counts: {
              warnings: 0,
              bodies: 0,
              joints: 0,
              actuators: 0,
              referencedAssets: 0,
              missingAssets: 0,
              fileSizeKb: 0,
            },
            blockers: ["Critter could not generate the MJCF XML."],
            warnings: [],
          },
        });
      }
    },
    [validateKeyframesForExport, updateSuggestedExportProjectName]
  );

  const buildPendingEnvironmentExport = React.useCallback(
    async (skipKeyframeNames = []) => {
      const skippedKeyframes = Array.isArray(skipKeyframeNames) ? skipKeyframeNames : [];
      const fileStoreState = useFileStore.getState();
      updateSuggestedExportProjectName(fileStoreState.projectName);
      const xml = await fileStoreState.actions.generateXmlFromState({
        startupPosePolicy: "currentScenePose",
        xmlNamingMode: "uuid",
        skipKeyframeNames: skippedKeyframes,
      });
      const sceneConfig = buildSceneConfig();
      let review = simplifyBlankProjectReview(
        buildMjcfExportReview(xml, fileStoreState.fileMap || {}, [])
      );
      if (await detectEnvConfigXmlFingerprintMismatch(xml)) {
        review = appendExportReviewWarning(
          review,
          "Model has changed since last export - remote servers may use a stale version."
        );
      }
      const referencedAssets = review.counts?.referencedAssets ?? 0;
      const pendingExport = {
        filename: "critter_engine_project.critter",
        lightFilename: "critter_engine_project.critterrefs",
        xml,
        sceneConfig,
        fileMap: fileStoreState.fileMap || {},
        review: {
          ...review,
          summary: review.counts?.isBlankProject
            ? review.summary
            : referencedAssets > 0
              ? ""
              : "This environment does not reference external assets.",
        },
      };
      try {
        const sizeEstimate = await estimateEnvironmentBundleSize({
          xml,
          sceneConfig,
          projectSnapshot: buildCurrentProjectSnapshot({ modelXmlPath: "model.xml" }),
          projectName: fileStoreState.projectName,
          fileMap: fileStoreState.fileMap || {},
          includeAssets: true,
        });
        pendingExport.review.counts.estimatedDownloadBytes = sizeEstimate.totalBytes;
        pendingExport.review.counts.estimatedAssetBytes = sizeEstimate.assetBytes;
        const lightSizeEstimate = await estimateEnvironmentBundleSize({
          xml,
          sceneConfig,
          projectSnapshot: buildCurrentProjectSnapshot({ modelXmlPath: "model.xml" }),
          projectName: fileStoreState.projectName,
          fileMap: fileStoreState.fileMap || {},
          includeAssets: false,
        });
        pendingExport.review.counts.estimatedLightDownloadBytes = lightSizeEstimate.totalBytes;
      } catch (error) {
        logger.warn("[main] failed to estimate Critter project download size:", error);
      }
      return pendingExport;
    },
    [updateSuggestedExportProjectName]
  );

  const createBlockedEnvironmentExport = React.useCallback((summary, blocker) => {
    return {
      filename: "critter_engine_project.critter",
      lightFilename: "critter_engine_project.critterrefs",
      xml: "",
      sceneConfig: {},
      fileMap: {},
      review: {
        status: "blocked",
        summary,
        counts: {
          warnings: 0,
          bodies: 0,
          joints: 0,
          actuators: 0,
          referencedAssets: 0,
          missingAssets: 0,
          fileSizeKb: 0,
        },
        blockers: [blocker],
        warnings: [],
      },
    };
  }, []);

  const downloadEnvironmentExport = React.useCallback(
    async (
      pendingExport,
      nextProjectName,
      { allowUnverifiableLocalProject = false, originalIntegrityReason } = {}
    ) => {
      const filenameBase = `${toExportFilenameBase(nextProjectName)}_critter_engine_project`;
      await downloadEnvironmentBundleFromExport({
        filename: `${filenameBase}.critter`,
        xml: applyProjectNameToMjcfXml(pendingExport.xml, nextProjectName),
        sceneConfig: pendingExport.sceneConfig,
        projectSnapshot: buildCurrentProjectSnapshot({ modelXmlPath: "model.xml" }),
        projectName: nextProjectName,
        fileMap: pendingExport.fileMap,
        includeAssets: true,
        allowUnverifiableLocalProject,
        originalIntegrityReason,
      });
    },
    []
  );

  const handleExportEnvironmentZip = React.useCallback(
    async (skipKeyframeNames = []) => {
      const skippedKeyframes = Array.isArray(skipKeyframeNames) ? skipKeyframeNames : [];
      if (skippedKeyframes.length === 0) {
        const invalid = validateKeyframesForExport();
        if (invalid.length > 0) {
          setPendingKeyframeMismatchExport({ type: "environment", invalid });
          return;
        }
      }
      try {
        setPendingEnvironmentExport(await buildPendingEnvironmentExport(skippedKeyframes));
      } catch (e) {
        logger.error("[main] failed to export Critter project:", e);
        updateSuggestedExportProjectName(useFileStore.getState().projectName);
        setPendingEnvironmentExport(
          createBlockedEnvironmentExport(
            "The environment could not be generated.",
            "Critter could not generate the project file."
          )
        );
      }
    },
    [
      buildPendingEnvironmentExport,
      createBlockedEnvironmentExport,
      validateKeyframesForExport,
      updateSuggestedExportProjectName,
    ]
  );

  const handleExportDeploymentBundle = React.useCallback(async () => {
    try {
      updateSuggestedExportProjectName(useFileStore.getState().projectName);
      const built = await buildDeploymentBundleZip({ filenameBase: "critter_deployment_bundle" });
      if (built.warnings.length > 0) {
        logger.warn("[main] built deployment bundle with warnings:", built.warnings);
      }
      setPendingDeploymentBundle(built);
    } catch (e) {
      logger.error("[main] failed to export deployment bundle:", e);
    }
  }, [updateSuggestedExportProjectName]);

  const handleDownloadPendingDeploymentBundle = React.useCallback(async () => {
    if (!pendingDeploymentBundle) return;
    const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
    fileActions.setProjectName(nextProjectName);
    try {
      const filenameBase = `${toExportFilenameBase(nextProjectName)}_deployment_bundle`;
      const rebuilt = await buildDeploymentBundleZip({ filenameBase });
      downloadBuiltDeploymentBundle(rebuilt);
      setPendingDeploymentBundle({ ...rebuilt, downloadComplete: true });
    } catch (e) {
      logger.error("[main] failed to download deployment bundle:", e);
    }
  }, [pendingDeploymentBundle, exportProjectName, projectName, fileActions]);

  const handleCancelPendingDeploymentBundle = React.useCallback(() => {
    setPendingDeploymentBundle(null);
  }, []);

  const handleDownloadPendingEnvironmentBundle = React.useCallback(async () => {
    if (!pendingEnvironmentExport) return;
    try {
      const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
      getProjectSaveActions().markProjectSaving("local-download");
      fileActions.setProjectName(nextProjectName);
      await downloadEnvironmentExport(pendingEnvironmentExport, nextProjectName);
      markCurrentProjectSaved();
      showProjectDownloadCompleteToast();
      setPendingEnvironmentExport({ ...pendingEnvironmentExport, downloadComplete: true });
      resolvePendingProjectSaveRequest(true);
    } catch (e) {
      const integrityReason = getProjectIntegrityFailureReason(e);
      if (integrityReason && confirmUnverifiableLocalProjectSave(integrityReason)) {
        try {
          const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
          await downloadEnvironmentExport(pendingEnvironmentExport, nextProjectName, {
            allowUnverifiableLocalProject: true,
            originalIntegrityReason: integrityReason,
          });
          markCurrentProjectSaved();
          showProjectDownloadCompleteToast();
          setPendingEnvironmentExport({ ...pendingEnvironmentExport, downloadComplete: true });
          resolvePendingProjectSaveRequest(true);
          return;
        } catch (retryError) {
          logger.error("[main] failed to export unverifiable Critter project:", retryError);
        }
      }
      getProjectSaveActions().markProjectSaveError(e);
      logger.error("[main] failed to export Critter project:", e);
      setPendingEnvironmentExport((current) => {
        if (!current) return current;
        const blocker =
          "Critter could not collect every referenced asset for the full project file.";
        const existingBlockers = current.review?.blockers || [];
        return {
          ...current,
          review: {
            ...current.review,
            status: "blocked",
            summary:
              "The environment was generated, but the full asset bundle could not be completed.",
            blockers: existingBlockers.includes(blocker)
              ? existingBlockers
              : [...existingBlockers, blocker],
          },
        };
      });
    }
  }, [
    pendingEnvironmentExport,
    exportProjectName,
    projectName,
    fileActions,
    downloadEnvironmentExport,
    markCurrentProjectSaved,
    resolvePendingProjectSaveRequest,
    showProjectDownloadCompleteToast,
  ]);

  const handleQuickDownloadEnvironmentBundle = React.useCallback(async () => {
    if (quickProjectSavePending) return false;
    const invalid = validateKeyframesForExport();
    if (invalid.length > 0) {
      setPendingKeyframeMismatchExport({ type: "environment", invalid });
      return false;
    }
    setQuickProjectSavePending(true);
    try {
      const pendingExport = await buildPendingEnvironmentExport();
      if (pendingExport.review?.status === "blocked") {
        setPendingEnvironmentExport(pendingExport);
        return false;
      }
      const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
      getProjectSaveActions().markProjectSaving("local-download");
      fileActions.setProjectName(nextProjectName);
      await downloadEnvironmentExport(pendingExport, nextProjectName);
      markCurrentProjectSaved();
      showProjectDownloadCompleteToast();
      resolvePendingProjectSaveRequest(true);
      return true;
    } catch (e) {
      getProjectSaveActions().markProjectSaveError(e);
      logger.error("[main] failed to quick-save Critter project:", e);
      updateSuggestedExportProjectName(useFileStore.getState().projectName);
      setPendingEnvironmentExport(
        createBlockedEnvironmentExport(
          "The project could not be saved.",
          "Critter could not complete the project download."
        )
      );
      return false;
    } finally {
      setQuickProjectSavePending(false);
    }
  }, [
    buildPendingEnvironmentExport,
    createBlockedEnvironmentExport,
    downloadEnvironmentExport,
    exportProjectName,
    fileActions,
    markCurrentProjectSaved,
    projectName,
    quickProjectSavePending,
    resolvePendingProjectSaveRequest,
    showProjectDownloadCompleteToast,
    updateSuggestedExportProjectName,
    validateKeyframesForExport,
  ]);

  const handleDownloadPendingEnvironmentLightBundle = React.useCallback(async () => {
    if (!pendingEnvironmentExport) return;
    const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
    getProjectSaveActions().markProjectSaving("local-download");
    fileActions.setProjectName(nextProjectName);
    const filenameBase = `${toExportFilenameBase(nextProjectName)}_critter_engine_project`;
    await downloadEnvironmentBundleFromExport({
      filename: `${filenameBase}.critterrefs`,
      xml: applyProjectNameToMjcfXml(pendingEnvironmentExport.xml, nextProjectName),
      sceneConfig: pendingEnvironmentExport.sceneConfig,
      projectSnapshot: buildCurrentProjectSnapshot({ modelXmlPath: "model.xml" }),
      projectName: nextProjectName,
      fileMap: pendingEnvironmentExport.fileMap,
      includeAssets: false,
    });
    markCurrentProjectSaved();
    showProjectDownloadCompleteToast();
    setPendingEnvironmentExport({ ...pendingEnvironmentExport, downloadComplete: true });
    resolvePendingProjectSaveRequest(true);
  }, [
    pendingEnvironmentExport,
    exportProjectName,
    projectName,
    fileActions,
    markCurrentProjectSaved,
    resolvePendingProjectSaveRequest,
    showProjectDownloadCompleteToast,
  ]);

  const handleCancelPendingEnvironmentExport = React.useCallback(() => {
    setPendingEnvironmentExport(null);
    resolvePendingProjectSaveRequest(false);
  }, [resolvePendingProjectSaveRequest]);

  const handleDownloadPendingMjcfExport = React.useCallback(() => {
    if (!pendingMjcfExport) return;
    const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
    fileActions.setProjectName(nextProjectName);
    const filenameBase = toExportFilenameBase(nextProjectName);
    const robotXml = applyProjectNameToMjcfXml(pendingMjcfExport.xml, nextProjectName);
    const baseXml = includeMjcfDesktopScene
      ? createStandaloneMjcfSceneXml(robotXml, nextProjectName)
      : createRobotOnlyMjcfXml(robotXml, nextProjectName);
    const xml = pendingMjcfExport.liftModelToFloor
      ? liftMjcfWorldBodies(baseXml, pendingMjcfExport.review?.floorClearance?.liftAmount)
      : baseXml;
    downloadTextFile(
      `${filenameBase}${includeMjcfDesktopScene ? "_scene" : ""}.xml`,
      xml,
      "application/xml"
    );
    setPendingMjcfExport({ ...pendingMjcfExport, downloadComplete: true });
  }, [pendingMjcfExport, exportProjectName, projectName, fileActions, includeMjcfDesktopScene]);

  const handleDownloadPendingMjcfBundle = React.useCallback(async () => {
    if (!pendingMjcfExport) return;
    try {
      const nextProjectName = normalizeExportProjectName(exportProjectName || projectName);
      fileActions.setProjectName(nextProjectName);
      const filenameBase = toExportFilenameBase(nextProjectName);
      const robotXml = applyProjectNameToMjcfXml(pendingMjcfExport.xml, nextProjectName);
      const liftAmount = pendingMjcfExport.liftModelToFloor
        ? pendingMjcfExport.review?.floorClearance?.liftAmount
        : 0;
      const bundleArgs = {
        filename: `${filenameBase}_mujoco_xml_assets_bundle.zip`,
        folderName: `${filenameBase}_mujoco_xml_assets_bundle`,
        xml: liftAmount > 0 ? liftMjcfWorldBodies(robotXml, liftAmount) : robotXml,
        fileMap: pendingMjcfExport.fileMap,
        sceneState: getSceneState(),
      };
      if (includeMjcfDesktopScene) {
        await downloadPortableMjcfSceneBundle({
          ...bundleArgs,
          xml: robotXml,
          projectName: nextProjectName,
          floorLiftAmount: liftAmount,
        });
      } else {
        await downloadPortableMjcfBundle(bundleArgs);
      }
      setPendingMjcfExport({ ...pendingMjcfExport, downloadComplete: true });
    } catch (e) {
      logger.error("[main] failed to export MJCF asset bundle:", e);
      setPendingMjcfExport((current) => {
        if (!current) return current;
        const blocker =
          "Critter could not collect every referenced asset for the XML + Assets bundle.";
        const existingBlockers = current.review?.blockers || [];
        return {
          ...current,
          review: {
            ...current.review,
            status: "blocked",
            summary: "The XML was generated, but the asset bundle could not be completed.",
            blockers: existingBlockers.includes(blocker)
              ? existingBlockers
              : [...existingBlockers, blocker],
          },
        };
      });
    }
  }, [pendingMjcfExport, exportProjectName, projectName, fileActions, includeMjcfDesktopScene]);

  const handleCancelPendingMjcfExport = React.useCallback(() => {
    setPendingMjcfExport(null);
  }, []);

  const setPendingMjcfLiftModelToFloor = React.useCallback((checked) => {
    setPendingMjcfExport((current) =>
      current ? { ...current, liftModelToFloor: Boolean(checked) } : current
    );
  }, []);

  React.useEffect(() => {
    const onProjectSaveRequest = (event) => {
      const detail = event.detail;
      if (!detail || typeof detail.resolve !== "function") return;
      if (detail.target === "cloud") return;
      detail.handled = true;
      resolvePendingProjectSaveRequest(false);
      pendingProjectSaveRequestRef.current = detail.resolve;
      void handleExportEnvironmentZip();
    };
    window.addEventListener(PROJECT_SAVE_REQUEST_EVENT, onProjectSaveRequest);
    return () => window.removeEventListener(PROJECT_SAVE_REQUEST_EVENT, onProjectSaveRequest);
  }, [handleExportEnvironmentZip, resolvePendingProjectSaveRequest]);

  const handleSkipMismatchedKeyframes = React.useCallback(() => {
    const pending = pendingKeyframeMismatchExport;
    if (!pending) return;
    const skipNames = pending.invalid.map((item) => item.name);
    setPendingKeyframeMismatchExport(null);
    if (pending.type === "environment") {
      void handleExportEnvironmentZip(skipNames);
    } else {
      void handleExportMjcfXml(skipNames);
    }
  }, [handleExportEnvironmentZip, handleExportMjcfXml, pendingKeyframeMismatchExport]);

  const handleAbortMismatchedKeyframes = React.useCallback(() => {
    setPendingKeyframeMismatchExport(null);
  }, []);

  const handleExportReviewFeedback = React.useCallback(() => {
    setPendingEnvironmentExport(null);
    setPendingMjcfExport(null);
    setPendingDeploymentBundle(null);
    onOpenFeedbackPanel();
  }, [onOpenFeedbackPanel]);

  const handleExportReviewUpdates = React.useCallback(() => {
    setPendingEnvironmentExport(null);
    setPendingMjcfExport(null);
    setPendingDeploymentBundle(null);
    onOpenUpdatesPanel();
  }, [onOpenUpdatesPanel]);

  return {
    pendingDeploymentBundle,
    pendingMjcfExport,
    includeMjcfDesktopScene,
    setIncludeMjcfDesktopScene,
    setPendingMjcfLiftModelToFloor,
    pendingEnvironmentExport,
    pendingKeyframeMismatchExport,
    quickProjectSavePending,
    exportProjectName,
    setExportProjectName,
    refreshSuggestedExportProjectName,
    handleExportMjcfXml,
    handleExportEnvironmentZip,
    handleQuickDownloadEnvironmentBundle,
    handleExportDeploymentBundle,
    handleDownloadPendingDeploymentBundle,
    handleCancelPendingDeploymentBundle,
    handleDownloadPendingEnvironmentBundle,
    handleDownloadPendingEnvironmentLightBundle,
    handleCancelPendingEnvironmentExport,
    handleDownloadPendingMjcfExport,
    handleDownloadPendingMjcfBundle,
    handleCancelPendingMjcfExport,
    handleSkipMismatchedKeyframes,
    handleAbortMismatchedKeyframes,
    handleExportReviewFeedback,
    handleExportReviewUpdates,
  };
}
